import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Input, Modal, Textarea } from "@/components/common";
import { useDeleteContact, useSaveContact } from "@/hooks/useToolkit";
import type { InsurerContact } from "@/types/toolkit";

export interface ContactTarget {
  insurerId: string;
  insurerName: string;
  /** null adds a new contact. */
  contact: InsurerContact | null;
}

function errorMessage(error: unknown, fallback: string) {
  return (error as { message?: string } | null)?.message ?? fallback;
}

export function ContactModal({
  target,
  onClose,
}: {
  target: ContactTarget | null;
  onClose: () => void;
}) {
  const save = useSaveContact();
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  // The modal stays mounted: fill the form each time it opens for another contact.
  const [openedFor, setOpenedFor] = useState<ContactTarget | null>(null);
  if (target !== openedFor) {
    setOpenedFor(target);
    const c = target?.contact;
    setFullName(c?.fullName ?? "");
    setPosition(c?.position ?? "");
    setPhone(c?.phone ?? "");
    setEmail(c?.email ?? "");
    setNote(c?.note ?? "");
  }

  const isNew = target?.contact === null;
  const valid =
    fullName.trim().length > 0 &&
    (phone.trim().length > 0 || email.trim().length > 0);

  function handleClose() {
    save.reset();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!target || !valid) {
      return;
    }
    save.mutate(
      {
        id: target.contact?.id ?? null,
        insurerId: target.insurerId,
        payload: {
          fullName: fullName.trim(),
          position: position.trim(),
          phone: phone.trim(),
          email: email.trim(),
          note: note.trim(),
        },
      },
      {
        onSuccess: (saved) => {
          toast.success(
            isNew
              ? `Dodano kontakt: ${saved.fullName}.`
              : `Zapisano zmiany: ${saved.fullName}.`,
          );
          handleClose();
        },
      },
    );
  }

  return (
    <Modal
      isOpen={target !== null}
      onClose={handleClose}
      title={isNew ? "Nowy kontakt" : "Edytuj kontakt"}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {save.error && (
          <Alert variant="error">
            {errorMessage(save.error, "Nie udało się zapisać kontaktu")}
          </Alert>
        )}

        <p className="text-sm text-dim">
          Towarzystwo:{" "}
          <span className="font-medium text-main">{target?.insurerName}</span>
        </p>

        <Input
          label="Imię i nazwisko"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
          }}
          required
          maxLength={120}
          autoComplete="off"
        />
        <Input
          label="Stanowisko / zakres (opcjonalnie)"
          value={position}
          onChange={(e) => {
            setPosition(e.target.value);
          }}
          placeholder="np. Manager regionalny, likwidacja szkód"
          maxLength={120}
          autoComplete="off"
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Telefon"
            type="tel"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            placeholder="np. 600 100 200"
            maxLength={40}
            autoComplete="off"
          />
          <Input
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            maxLength={200}
            autoComplete="off"
          />
        </div>
        <Textarea
          label="Notatka (opcjonalnie)"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
          }}
          placeholder="np. dostępny pn–pt 8–16, w sprawach OC/AC"
          rows={2}
          maxLength={500}
        />
        {!valid && fullName.trim().length > 0 && (
          <p className="text-xs text-dim">
            Podaj numer telefonu lub adres e-mail.
          </p>
        )}

        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button type="submit" isLoading={save.isPending} disabled={!valid}>
            {isNew ? "Dodaj kontakt" : "Zapisz"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function DeleteContactModal({
  contact,
  onClose,
}: {
  contact: InsurerContact | null;
  onClose: () => void;
}) {
  const remove = useDeleteContact();

  function handleClose() {
    remove.reset();
    onClose();
  }

  return (
    <Modal isOpen={contact !== null} onClose={handleClose} title="Usuń kontakt">
      <div className="space-y-4">
        {remove.isError && (
          <Alert variant="error">
            {errorMessage(remove.error, "Nie udało się usunąć kontaktu")}
          </Alert>
        )}
        <div className="flex gap-3 rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
          <AlertTriangle
            size={20}
            className="shrink-0 text-red-600 dark:text-red-400"
            aria-hidden="true"
          />
          <p className="text-sm text-red-800 dark:text-red-300">
            Kontakt <span className="font-semibold">{contact?.fullName}</span>{" "}
            zniknie z Podręcznika Agenta dla wszystkich.
          </p>
        </div>
        <div className="flex justify-end gap-3 border-t border-edge-soft pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            variant="danger"
            isLoading={remove.isPending}
            onClick={() => {
              if (contact) {
                remove.mutate(contact.id, {
                  onSuccess: () => {
                    toast.success("Usunięto kontakt.");
                    handleClose();
                  },
                });
              }
            }}
          >
            Usuń kontakt
          </Button>
        </div>
      </div>
    </Modal>
  );
}
